import { Fragment } from 'react';
import { Field } from 'formik';
import cn from 'classnames';

import styles from '../components/_pages/Step6/Step6.module.scss';
import cms from '../data/cms.json';
import { CommonDataProps, QuestionProps } from '../types/initData';
import { validateField } from './validations';
import { constructSelectOptions, resolveFieldText } from './externalDataHandling';
import { ToggleField } from '../components/ToggleField/ToggleField';
import { TextField } from '../components/TextField/TextField';

import { Grid } from '../components/Grid/Grid';
import { Label } from '../components/Label/Label';
import { Select } from '../components/Select/Select';
import { FormItemError } from '../components/FormItemError/FormItemError';
import { ValuesProps } from '../components/_pages/Step6/Step6';
import { ContextProps } from '../context/AppContext';
import { AmountButton } from '../components/AmountButton/AmountButton';
import { FormikValueCustomProps } from '../types/common';

// ### @TODO: Temporary ugly solution for diopter placeholder
// Get placeholder data ideally from API, when available

const addDioptrePlaceholder = (id: string) => {
    if (
        id.startsWith('O_EYD01_NO_DIOPTRE_R') ||
        id.startsWith('O_EYD01_NO_DIOPTRE_L') ||
        id.startsWith('O_EYD02_NO_DIOPTRE_R') ||
        id.startsWith('O_EYD02_NO_DIOPTRE_L')
    ) {
        return 'např. 1,25';
    }
    return '';
};

// ### Function returns one question (JSX) in questionnaire form

export const renderQuestion = (
    q: QuestionProps,
    commonData: CommonDataProps,
    errors: ValuesProps,
    id: string,
    ctx: ContextProps,
    setFieldValue?: (field: string, value: FormikValueCustomProps, shouldValidate?: boolean) => void,
    index?: number
) => {
    const currentMA = ctx.multiAnswer?.[q.Code];

    return (
        <div key={q.Code} className="question-item" data-test={`questionItem[${q.Code}]`}>
            <Label help={q.Help} data-test={`questionItem[${q.Code}]Label`}>
                {q.Name}
            </Label>
            <Grid cols="auto" className={styles.questionWrapper}>
                <div
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                    data-test={`questionItem[${q.Code}]Content`}
                >
                    {
                        // generate empty div due +- buttons align
                        q.IsMultiAnswer && <div className={styles.amountButtons}></div>
                    }

                    {q.PossibleAnswersIds.length > 3 &&
                    (q.QuestionStyleId === 3 || q.QuestionStyleId === 10 || q.QuestionAnswerFormatId === 1) ? (
                        // Input type: select
                        <Field
                            id={id}
                            name={id}
                            className={cn(styles.select)}
                            options={constructSelectOptions(commonData, q.PossibleAnswersIds)}
                            component={Select}
                            validate={(value: string | number) => validateField(value, false)}
                            error={errors[id]}
                            data-test={`questionItem[${q.Code}]`}
                        />
                    ) : q.QuestionAnswerFormatId === 5 ? (
                        // Input type: float
                        <TextField
                            key={id}
                            name={id}
                            variant="decimal"
                            placeholder={addDioptrePlaceholder(id)}
                            error={errors[id]}
                            data-test={`questionItem[${q.Code}]`}
                        />
                    ) : q.QuestionAnswerFormatId === 3 ? (
                        // Input type: number
                        <TextField
                            key={id}
                            name={id}
                            variant="number"
                            className="field-small"
                            max={1000000000}
                            error={errors[id]}
                            data-test={`questionItem[${q.Code}]`}
                        />
                    ) : (
                        <div className="toggle-list" data-test={`questionItem[${q.Code}]ToggleList`}>
                            {q.PossibleAnswersIds?.map((a) => (
                                <Fragment key={`${id}-${a}`}>
                                    <ToggleField
                                        name={id}
                                        id={`${id}-${a}`}
                                        variant="radio"
                                        value={resolveFieldText(a, commonData)}
                                        error={errors[id]}
                                        data-test={`questionItem[${q.Code}]Toogle[${a}]`}
                                    />
                                </Fragment>
                            ))}
                        </div>
                    )}
                    {/* ### Add increase and decrease buttons form multiAnswers */}
                    {q.IsMultiAnswer && (
                        <div className={styles.amountButtons}>
                            {(!currentMA || currentMA[currentMA.length - 1] === index) && (
                                <AmountButton
                                    className="addMore"
                                    title={cms.common.addItemButton}
                                    actionType="increase"
                                    onClick={() => {
                                        const current = { ...ctx.multiAnswer };
                                        const data = current[q.Code] || [0];
                                        ctx.setMultiAnswer({
                                            ...current,
                                            [q.Code]: [...data, Math.max(...data, -1) + 1],
                                        });
                                    }}
                                    data-test={`questionItem[${q.Code}]AddMore`}
                                />
                            )}

                            {ctx.multiAnswer[q.Code]?.length > 1 && (
                                <AmountButton
                                    className="addMore"
                                    title={cms.common.removeItemButton}
                                    actionType="decrease"
                                    onClick={() => {
                                        setFieldValue && setFieldValue(`${q.Code}_MULTI_${index}`, undefined);
                                        const current = { ...ctx.multiAnswer };
                                        ctx.setMultiAnswer({
                                            ...current,
                                            [q.Code]: current[q.Code].filter((item: number) => item !== index),
                                        });
                                    }}
                                    data-test={`questionItem[${q.Code}]AddMore`}
                                />
                            )}
                        </div>
                    )}
                </div>
                {errors[id] && <FormItemError text={errors[id]} data-test={`questionItem[${q.Code}]Error`} />}
            </Grid>
        </div>
    );
};
